import { createRoute, Navigate, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { authenticatedRoute } from '@/authenticated-route';
import type { FloatButtonLoaderData } from '@/components/float-button';
import FullPageLoadingSpinner from '@/components/ui/full-page-loading-spinner';
import { CdrPatientCardSource } from '@/features/patient-360/components/patient-card-source';
import { MENU_ITEMS_MAP } from '@/features/patient-360/consts/menu';
import { PATIENT_360_ROUTES } from '@/features/patient-360/consts/routes';
import { getQueryPatientCardOptions } from '@/features/patient-360/services/use-query-patient-card';
import { getQueryUserFavFuncOptions } from '@/features/patient-360/services/use-query-user-fav-func';
import { sharedSearchParams } from '@/features/patient-360/types/route-schema';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { assertNonNullable } from '@/lib/utils';

export const patient360ViewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: PATIENT_360_ROUTES.patientView,
  staticData: {
    parent: PATIENT_360_ROUTES.index,
    home: null,
    patientCardSource: CdrPatientCardSource,
  },
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(
    sharedSearchParams.extend({
      tab: z
        .enum(['function-menu', 'patient-detail'])
        .optional()
        .default('function-menu'),
      mrn: z.string().min(1),
    }),
  ),
  errorComponent: () => <Navigate replace to={PATIENT_360_ROUTES.index} />,
  loaderDeps: ({ search }) => ({ mrn: search.mrn }),
  loader: async ({ params, deps: { mrn }, context: { queryClient } }) => {
    const queryUserFavFuncOptions = getQueryUserFavFuncOptions();
    const [patient, userFavFuncs] = await Promise.all([
      queryClient.ensureQueryData(getQueryPatientCardOptions(mrn)),
      queryClient.ensureQueryData(queryUserFavFuncOptions),
    ]);
    if (!patient) {
      throw redirect({ to: PATIENT_360_ROUTES.index });
    }

    assertNonNullable(queryUserFavFuncOptions.queryFn);
    const floatButton: FloatButtonLoaderData['floatButton'] = {
      queryKey: queryUserFavFuncOptions.queryKey satisfies readonly string[],
      userFavFuncs,
      itemsMap: Object.fromEntries(
        Object.entries(MENU_ITEMS_MAP).map(([key, def]) => [
          key,
          { icon: def.icon, i18nKey: def.i18nKey, to: def.route },
        ]),
      ),
    };

    return {
      patientId: params.patientId,
      floatButton,
    };
  },
  pendingComponent: FullPageLoadingSpinner,
}).lazy(() => import('./patient-360-view').then(({ Route }) => Route));
